/**
 * Single DNS label rules aligned with the Cloudflare Worker (LDH: 1–63 chars, no leading/trailing hyphen).
 */
export function isValidTenantSubdomainLabel(s: string): boolean {
  if (s.length < 1 || s.length > 63) return false;
  return /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i.test(s);
}

/** Tenant slug from `X-Tenant` on a Response (e.g. after `fetch()`). Not available on initial document navigation. */
export function getTenantFromResponseHeaders(headers: Headers): string | null {
  const raw = headers.get("X-Tenant")?.trim();
  if (!raw || !isValidTenantSubdomainLabel(raw) || RESERVED.has(raw.toLowerCase())) return null;
  return raw;
}

/**
 * When previewing on `*.pages.dev`, the Worker may add `?store=`; use only on preview hosts (not production tenant URLs).
 */
export function getTenantSlugFromPagesPreviewQuery(searchParams: URLSearchParams): string | null {
  const raw = searchParams.get("store")?.trim().toLowerCase();
  if (!raw || !isValidTenantSubdomainLabel(raw) || RESERVED.has(raw)) return null;
  return raw;
}

function isPagesPreviewHostname(hostname: string): boolean {
  const h = hostname.toLowerCase();
  return h.endsWith(".pages.dev") || h.endsWith(".cloudflarepages.com");
}

/**
 * Raw subdomain label from hostname (first label when 3+ dot segments).
 * For production tenant URLs, prefer {@link getTenantStoreSubdomain}: it matches the
 * configured apex (e.g. `reed.usefastsell.com`) and avoids treating
 * `hostname.split(".")[0]` as the tenant on unrelated multi-label hosts.
 */
export function getSubdomain(): string | null {
  if (typeof window === "undefined") return null;

  const hostname = window.location.hostname;

  if (hostname.includes("localhost")) {
    if (hostname === "localhost" || hostname.startsWith("127.")) return null;
    if (hostname.endsWith(".localhost")) {
      const sub = hostname.split(".")[0];
      return sub && sub !== "localhost" ? sub : null;
    }
    return null;
  }

  const parts = hostname.split(".");
  return parts.length > 2 ? parts[0] : null;
}

const RESERVED = new Set([
  "www",
  "app",
  "api",
  "admin",
  "cdn",
  "mail",
  "ftp",
  "staging",
  "preview",
  "usefastsell",
]);

/** Reserved first labels (must stay in sync with DB trigger / Worker). */
export function isReservedTenantLabel(label: string): boolean {
  return RESERVED.has(label.trim().toLowerCase());
}

function normalizeRootDomain(raw: string): string {
  return raw.trim().toLowerCase().replace(/^\./, "");
}

/**
 * Public store hostname suffix, e.g. usefastsell.com.
 * Only hostnames under this domain are treated as tenant storefronts (avoids *.netlify.app false positives).
 */
export function getTenantRootDomain(): string {
  return normalizeRootDomain(import.meta.env.VITE_PUBLIC_TENANT_ROOT_DOMAIN || "usefastsell.com");
}

/**
 * Returns store slug when the current host is a tenant storefront (e.g. reed.usefastsell.com → "reed").
 * Apex and www return null. Unknown hosts (not under tenant root) return null.
 */
export function getTenantStoreSubdomain(): string | null {
  if (typeof window === "undefined") return null;

  const hostname = window.location.hostname.toLowerCase();
  const root = getTenantRootDomain();

  if (!root) return null;

  // Dev: reed.localhost → reed
  if (hostname.endsWith(".localhost") && hostname !== "localhost") {
    const sub = hostname.split(".")[0];
    if (!sub || RESERVED.has(sub)) return null;
    return sub;
  }

  if (hostname === "localhost" || hostname.startsWith("127.")) return null;

  if (hostname === root || hostname === `www.${root}`) return null;

  if (!hostname.endsWith(`.${root}`)) return null;

  const sub = hostname.slice(0, -(root.length + 1));
  if (!sub || sub.includes(".") || RESERVED.has(sub)) return null;
  if (!isValidTenantSubdomainLabel(sub)) return null;

  return sub;
}

/**
 * Tenant slug for the storefront: hostname on `*.tenantRoot`, or `?store=` on Pages preview hosts only.
 */
export function getTenantStoreSubdomainOrPreview(
  searchParams: URLSearchParams,
): string | null {
  const fromHost = getTenantStoreSubdomain();
  if (fromHost) return fromHost;
  if (typeof window === "undefined") return null;
  if (!isPagesPreviewHostname(window.location.hostname)) return null;
  return getTenantSlugFromPagesPreviewQuery(searchParams);
}
